import type { Geo } from '@/api/types/ProviderApi'
import { DatePicker } from '@/components/DatePicker'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import type {
	StudioSection,
	TimeChip,
	WeekView,
} from '@/features/scheduling/availability'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'
import { DayStrip } from './DayStrip'
import { DayTimes, Quiet } from './DayTimes'
import { GeoSwitcher } from './GeoSwitcher'
import { TYPE_INSET } from './layout'

/**
 * "5 studios", or "1 studio" for a city with one.
 *
 * A count and its noun, which would not be worth a helper anywhere it is said once — but a city
 * with a single studio is a real city rather than a hypothetical one, and "the 1 studios" is the
 * kind of thing that ships.
 */
const studios = (view: WeekView) =>
	`${view.studios.length} ${view.studios.length === 1 ? 'studio' : 'studios'}`

/**
 * One step of a week, as a white disc.
 *
 * Blush on the pink: a fill says these are controls, which bare chevrons could not — they relied
 * on a chevron being self-evidently pressable and on slop a finger cannot see — and blush says it
 * a step below white. White is what this screen gives a thing being offered: a time, a day, a way
 * out. These two only move the week the offers are in, so they sit in the tone the studio cards
 * are drawn in rather than the one their contents are.
 *
 * Not a ring: the cross above just gave one up for reading as a second, weaker outline over the
 * rule, and drawing one here would put it back eight points lower.
 */
const ARROW = 36

const Arrow = ({
	back,
	label,
	disabled,
	onPress,
}: { back?: boolean; label: string; disabled: boolean; onPress: () => void }) => (
	<Pressable
		onPress={onPress}
		disabled={disabled}
		accessibilityRole="button"
		accessibilityLabel={label}
		// Four each way, which takes a thirty-six point disc to a forty-four point target without
		// the two of them reaching each other across the eight between.
		hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
		className="items-center justify-center rounded-full"
		// The dim goes in `style`, not in a class. Pressable composes an animated style that
		// always carries an opacity for its press feedback, and an inline style beats a class —
		// so `opacity-40` on this was never drawn, and the arrow at the end of the booking window
		// looked exactly like one that still had somewhere to go. Only set when disabled, so the
		// press animation still has the property to itself the rest of the time.
		// A disabled arrow gives up its fill rather than fading.
		//
		// Blush at forty percent over the pink is a nineteen-point shift in two channels, which a
		// screenshot can measure and an eye cannot. Without the fill there is no button there at
		// all — only a faint mark where one would be — which is the state being described.
		//
		// In `style` and not a class either way: Pressable composes an animated style carrying an
		// opacity for its press feedback, and an inline style beats a class, so an `opacity-*`
		// class on a Pressable is silently dropped.
		style={[
			{ width: ARROW, height: ARROW },
			disabled ? undefined : { backgroundColor: colors.blush },
		]}
		haptic={!disabled}
	>
		<Glyph
			name="chevron"
			size={16}
			color={disabled ? colors.edge : colors.ink}
			rotate={back ? 180 : 0}
		/>
	</Pressable>
)

export const SchedulePicker = ({
	view,
	bounds,
	selectedKey,
	isCalendarOpen,
	chosenDate,
	geos,
	geoId,
	onToggleCalendar,
	onWeekStep,
	onPickDate,
	onChooseDay,
	onChooseGeo,
	onSelect,
	onOpenStudio,
}: {
	view: WeekView
	bounds: { earliestBookableDate: string; maxBookableDate: string }
	selectedKey?: string
	isCalendarOpen: boolean
	chosenDate?: string
	geos: Geo[]
	geoId: number
	onToggleCalendar: () => void
	onWeekStep: (step: -1 | 1) => void
	onPickDate: (date: string) => void
	onChooseDay: (date: string) => void
	onChooseGeo: (geoId: number) => void
	onSelect: (chip: TimeChip) => void
	onOpenStudio: (studio: StudioSection) => void
}) => (
	<VStack gap={16}>
		{/* The week is the heading, and the heading is the disclosure.
		
		    The week rather than the month, because the week is what the screen is showing — and
		    because the label can say "This week" and "Next week", which is orientation the row
		    of dates in the grid cannot give. Only when it is neither does it fall back to the
		    span, and that is the one case where it repeats the dates below it.
		    
		    Pressing it unfolds the month in place. No sheet and no glyph in a corner: this screen
		    had three controls for two decisions, and the third was a second way to do what the
		    label itself can. */}
		{/* The heading takes the inset the cards do not, and the same one the line at the foot
		    takes.
		    
		    The page gives everything eighteen points, and a card fills them: its edge is a
		    straight run of colour, so eighteen reads as eighteen. Type has no edge, and the cards'
		    corners curve away from theirs, so the same measure left the week and the arrows
		    looking pushed against the glass while the cards looked held off it.
		    
		    A card's own padding, which is where a studio's name starts — so the week, the line at
		    the foot and a card's title all begin on one line, and the only thing starting at the
		    page's edge is a panel. */}
		{/* The extra under it is the heading's own, not the stack's: the week and the city are one
		    block, and a block wants more air beneath it than the cards below want between them. */}
		<VStack gap={4} style={{ paddingHorizontal: TYPE_INSET, marginBottom: 8 }}>
			<HStack className="items-center justify-between">
				<Pressable
					onPress={onToggleCalendar}
					accessibilityRole="button"
					accessibilityState={{ expanded: isCalendarOpen }}
					accessibilityLabel={`${view.label}. Choose a week`}
					hitSlop={8}
					className="shrink"
				>
					<HStack className="items-center" gap={9}>
						{/* One line, scaled to fit rather than wrapped.
						
						    The longest this gets is a week crossing a month — "Nov 30 – Dec 6".
						    It cannot be longer: a Monday-to-Sunday week that crosses one always
						    ends on the 1st to the 6th, so the second date is always a single
						    digit. At that length it clears the arrows by thirty points at 402,
						    twenty-one at 393, and three and a half at 375 — which is not clearance,
						    it is luck. Below that it collides. Shrinking the type is the only
						    answer that holds at every width: the arrows are a fixed size and the
						    label is the part that can give. */}
						<Header variant="h2" numberOfLines={1} adjustsFontSizeToFit>
							{view.label}
						</Header>
						{/* A calendar rather than a chevron. A chevron says the heading expands;
						    the mark says what it expands into, which is what a member is after
						    when they want a week that is not next to this one. There is no
						    turned version of it: once it is open, the calendar underneath says
						    so. */}
						<Glyph name="calendar" size={22} color={colors.ink} />
					</HStack>
				</Pressable>
				{/* One step either way, beside the week they step. Under the card they would have
				    to be given room out of the seven day columns, and a day column is already the
				    narrowest thing on the screen. */}
				<HStack className="items-center" gap={8}>
					<Arrow
						back
						label="Previous week"
						disabled={!view.canGoBack}
						onPress={() => onWeekStep(-1)}
					/>
					<Arrow
						label="Next week"
						disabled={!view.canGoForward}
						onPress={() => onWeekStep(1)}
					/>
				</HStack>
			</HStack>
			<GeoSwitcher geos={geos} selectedGeoId={geoId} onSelect={onChooseGeo} />
		</VStack>

		{/* The week's seven days, always. The month is behind the heading, in a sheet.
		
		    Unfolded in place it pushed everything under it down by a calendar's height and then
		    pulled it back up, so the studios a member was reading moved twice for a control they
		    might only have been glancing at. A sheet is the app's own answer to "a second thing,
		    over the first", and it is what every other picker here opens into. */}
		<View className="rounded-note bg-white p-3">
			<DayStrip view={view} chosen={chosenDate} onChoose={onChooseDay} />
		</View>

		{view.isEmpty ? (
			/* A week with nothing is the empty day's block one size up, rather than a photograph
			   with the brand at full voice: that made an occasion of a quiet week, and a member
			   meets the same kind of answer whether the gap is a day or a week. */
			<Quiet
				title="No Openings This Week"
				body={
					view.canGoForward
						? `No available times at any of the ${studios(view)} in the city.`
						: // The only state on this screen with no way out, because there is none:
						  // the week after this one is past the booking window. Said, rather than
						  // left to be inferred from a button that is not there.
						  `No available times at any of the ${studios(
								view
						  )} in the city, and this is the last week open for booking.`
				}
				ways={[]}
				onChooseDay={onChooseDay}
				onNextWeek={view.canGoForward ? () => onWeekStep(1) : undefined}
			/>
		) : (
			<DayTimes
				view={view}
				date={chosenDate}
				selectedKey={selectedKey}
				onSelect={onSelect}
				onOpenStudio={onOpenStudio}
				onChooseDay={onChooseDay}
				onNextWeek={() => onWeekStep(1)}
			/>
		)}

		{view.showsTimezoneNote && (
			<Text size="body2" tone="soft">
				All times are {view.timezoneLabel} time.
			</Text>
		)}

		<Sheet open={isCalendarOpen} onClose={onToggleCalendar}>
			<View className="pt-2">
				<SheetTitle glyph="calendar">Pick a Week</SheetTitle>
				{/* The month sits on a card, and the card is blush because the sheet under it is
				    white — the calendar's own default is white, which on a white sheet is no card
				    at all. Its surface is the caller's to choose, so this is one card rather than
				    a blush ring drawn around a white one. */}
				<View className="px-4 pb-8">
					<DatePicker
						value={chosenDate ?? view.weekStart}
						onChange={onPickDate}
						earliest={bounds.earliestBookableDate}
						latest={bounds.maxBookableDate}
						highlight="week"
						surface="bg-blush"
						years={false}
					/>
				</View>
			</View>
		</Sheet>
	</VStack>
)
